import { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Plus, Trash2, KeyRound, History } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { AdminSidebar } from '@/components/AdminSidebar';
import { verifyAdmin, getAccounts, createAccount, updateAccount, deleteAccount, getAdminWriters, getLoginLogs } from '@/lib/api';
import { toast } from 'sonner';

export default function AdminAccounts() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [writers, setWriters] = useState([]);
  const [loginLogs, setLoginLogs] = useState([]);
  const [deleteId, setDeleteId] = useState(null);
  const [resetId, setResetId] = useState(null);
  const [resetPassword, setResetPassword] = useState('');
  const [form, setForm] = useState({ username: '', password: '', writer_id: '' });

  const fetchData = useCallback(async () => {
    try {
      const [accs, ws, logs] = await Promise.all([getAccounts(), getAdminWriters(), getLoginLogs(100)]);
      setAccounts(accs);
      setWriters(ws);
      setLoginLogs(logs);
    } catch (error) {
      toast.error('Failed to load accounts');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const checkAuth = async () => {
      const user = await verifyAdmin();
      if (!user) {
        navigate('/admin');
        return;
      }
      if (user.role && user.role !== 'admin') {
        navigate('/admin/dashboard');
        return;
      }
      fetchData();
    };
    checkAuth();
  }, [navigate, fetchData]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.username.trim() || !form.password || !form.writer_id) {
      toast.error('Username, password and writer profile are required');
      return;
    }
    setSaving(true);
    try {
      await createAccount(form);
      toast.success('Writer login created');
      setForm({ username: '', password: '', writer_id: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create account');
    } finally {
      setSaving(false);
    }
  };

  const handleResetPassword = async () => {
    if (!resetPassword || resetPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    try {
      await updateAccount(resetId, { password: resetPassword });
      toast.success('Password updated');
      setResetId(null);
      setResetPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update password');
    }
  };

  const handleToggle = async (acc, enabled) => {
    try {
      const updated = await updateAccount(acc.id, { disabled: !enabled });
      setAccounts(accounts.map(a => a.id === acc.id ? updated : a));
      toast.success(enabled ? `${acc.username} login enabled` : `${acc.username} login disabled`);
    } catch (error) {
      toast.error('Failed to update login status');
    }
  };

  const handleDelete = async () => {
    try {
      await deleteAccount(deleteId);
      toast.success('Account deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete account');
    } finally {
      setDeleteId(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black" data-testid="admin-accounts-page">
      <AdminSidebar active="accounts" />

      <main className="lg:ml-64 p-6 lg:p-8 max-w-5xl">
        <h1 className="font-heading text-3xl font-black text-white uppercase mb-2">Writer Logins</h1>
        <p className="font-body text-text-secondary mb-8">
          Writers log in at <span className="text-[#CCFF00] font-mono">/admin</span> with these credentials. They can publish articles under their linked profile and edit or delete their own articles for 10 minutes after publishing.
        </p>

        {/* Create form */}
        <form onSubmit={handleCreate} className="bg-surface border border-zinc-800 p-6 mb-8 space-y-4" data-testid="create-account-form">
          <p className="font-mono text-xs text-text-secondary uppercase">New Writer Login</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label className="text-white">Username</Label>
              <Input
                value={form.username}
                onChange={(e) => setForm({ ...form, username: e.target.value })}
                placeholder="e.g. maxfan01"
                className="mt-2 bg-black border-zinc-800 text-white focus:border-[#CCFF00]"
                data-testid="account-username-input"
              />
            </div>
            <div>
              <Label className="text-white">Password</Label>
              <Input
                type="password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder="Min 6 characters"
                className="mt-2 bg-black border-zinc-800 text-white focus:border-[#CCFF00]"
                data-testid="account-password-input"
              />
            </div>
            <div>
              <Label className="text-white">Writer Profile</Label>
              <Select value={form.writer_id || undefined} onValueChange={(value) => setForm({ ...form, writer_id: value })}>
                <SelectTrigger className="mt-2 bg-black border-zinc-800 text-white" data-testid="account-writer-select">
                  <SelectValue placeholder="Link to writer..." />
                </SelectTrigger>
                <SelectContent className="bg-surface border-zinc-800">
                  {writers.map((w) => (
                    <SelectItem key={w.id} value={w.id} className="text-white hover:bg-[#CCFF00]/10">{w.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          {writers.length === 0 && (
            <p className="text-xs text-text-secondary">
              No writer profiles yet. <Link to="/admin/writer/new" className="text-[#CCFF00] hover:underline">Create one first</Link>.
            </p>
          )}
          <Button type="submit" disabled={saving} className="bg-[#CCFF00] text-black hover:bg-[#b3e600] font-bold uppercase tracking-wide" data-testid="create-account-button">
            <Plus className="w-4 h-4 mr-2" /> {saving ? 'Creating...' : 'Create Login'}
          </Button>
        </form>

        {/* Accounts list */}
        <div className="bg-surface border border-zinc-800 overflow-x-auto">
          {accounts.length > 0 ? (
            <table className="w-full">
              <thead>
                <tr className="border-b border-zinc-800">
                  <th className="text-left p-4 font-mono text-xs text-text-secondary uppercase">Username</th>
                  <th className="text-left p-4 font-mono text-xs text-text-secondary uppercase">Writer Profile</th>
                  <th className="text-left p-4 font-mono text-xs text-text-secondary uppercase">Login</th>
                  <th className="text-left p-4 font-mono text-xs text-text-secondary uppercase hidden md:table-cell">Articles</th>
                  <th className="text-left p-4 font-mono text-xs text-text-secondary uppercase hidden lg:table-cell">Last Login</th>
                  <th className="text-left p-4 font-mono text-xs text-text-secondary uppercase hidden sm:table-cell">Created</th>
                  <th className="text-right p-4 font-mono text-xs text-text-secondary uppercase">Actions</th>
                </tr>
              </thead>
              <tbody>
                {accounts.map((acc) => (
                  <tr key={acc.id} className="border-b border-zinc-800/50" data-testid={`account-row-${acc.id}`}>
                    <td className="p-4 font-body font-medium text-white">{acc.username}</td>
                    <td className="p-4 font-body text-text-secondary">{acc.writer_name}</td>
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={!acc.disabled}
                          onCheckedChange={(enabled) => handleToggle(acc, enabled)}
                          data-testid={`toggle-account-${acc.id}`}
                        />
                        <span className={`font-mono text-xs uppercase ${acc.disabled ? 'text-red-400' : 'text-[#CCFF00]'}`}>
                          {acc.disabled ? 'Disabled' : 'Enabled'}
                        </span>
                      </div>
                    </td>
                    <td className="p-4 font-mono text-sm text-white hidden md:table-cell" data-testid={`account-articles-${acc.id}`}>{acc.article_count ?? 0}</td>
                    <td className="p-4 font-mono text-xs text-text-secondary hidden lg:table-cell" data-testid={`account-last-login-${acc.id}`}>
                      {acc.last_login ? new Date(acc.last_login).toLocaleString() : 'Never'}
                    </td>
                    <td className="p-4 font-mono text-sm text-text-secondary hidden sm:table-cell">{acc.created_at ? new Date(acc.created_at).toLocaleDateString() : ''}</td>
                    <td className="p-4">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-text-secondary hover:text-white hover:bg-white/10"
                          onClick={() => { setResetId(acc.id); setResetPassword(''); }}
                          data-testid={`reset-password-${acc.id}`}
                        >
                          <KeyRound className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-text-secondary hover:text-red-400 hover:bg-red-500/10"
                          onClick={() => setDeleteId(acc.id)}
                          data-testid={`delete-account-${acc.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="p-8 text-center font-body text-text-secondary">No writer logins yet</p>
          )}
        </div>

        {/* Login Log */}
        <div className="mt-12">
          <div className="flex items-center gap-2 mb-4">
            <History className="w-5 h-5 text-[#CCFF00]" />
            <h2 className="font-heading text-xl font-black text-white uppercase">Login Log</h2>
            <span className="font-mono text-xs text-text-secondary">(kept forever, latest 100 shown)</span>
          </div>
          <div className="bg-surface border border-zinc-800 overflow-x-auto" data-testid="login-log-table">
            {loginLogs.length > 0 ? (
              <table className="w-full">
                <thead>
                  <tr className="border-b border-zinc-800">
                    <th className="text-left p-3 font-mono text-xs text-text-secondary uppercase">Time</th>
                    <th className="text-left p-3 font-mono text-xs text-text-secondary uppercase">User</th>
                    <th className="text-left p-3 font-mono text-xs text-text-secondary uppercase">Role</th>
                    <th className="text-left p-3 font-mono text-xs text-text-secondary uppercase">Status</th>
                    <th className="text-left p-3 font-mono text-xs text-text-secondary uppercase hidden md:table-cell">IP</th>
                    <th className="text-left p-3 font-mono text-xs text-text-secondary uppercase hidden sm:table-cell">Device</th>
                  </tr>
                </thead>
                <tbody>
                  {loginLogs.map((log) => (
                    <tr key={log.id} className="border-b border-zinc-800/50" data-testid={`login-log-row-${log.id}`}>
                      <td className="p-3 font-mono text-xs text-text-secondary whitespace-nowrap">{new Date(log.logged_in_at).toLocaleString()}</td>
                      <td className="p-3 font-body text-sm text-white">
                        {log.username}
                        {log.writer_name && <span className="text-text-secondary"> ({log.writer_name})</span>}
                      </td>
                      <td className="p-3">
                        {log.role ? (
                          <span className={`inline-block px-2 py-0.5 text-[10px] font-bold uppercase ${log.role === 'admin' ? 'bg-[#CCFF00] text-black' : 'bg-zinc-800 text-white'}`}>
                            {log.role}
                          </span>
                        ) : (
                          <span className="font-mono text-xs text-text-secondary">—</span>
                        )}
                      </td>
                      <td className="p-3">
                        {log.success === false ? (
                          <span className="inline-block px-2 py-0.5 text-[10px] font-bold uppercase bg-red-500/15 text-red-400 border border-red-500/30" title={log.reason || 'Failed'} data-testid={`login-log-failed-${log.id}`}>
                            Failed
                          </span>
                        ) : (
                          <span className="inline-block px-2 py-0.5 text-[10px] font-bold uppercase bg-[#CCFF00]/10 text-[#CCFF00] border border-[#CCFF00]/30">
                            Success
                          </span>
                        )}
                      </td>
                      <td className="p-3 font-mono text-xs text-text-secondary hidden md:table-cell">{log.ip}</td>
                      <td className="p-3 font-body text-sm text-text-secondary hidden sm:table-cell">{log.browser} / {log.device}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="p-8 text-center font-body text-text-secondary">No logins recorded yet</p>
            )}
          </div>
        </div>
      </main>

      {/* Reset password dialog */}
      <AlertDialog open={!!resetId} onOpenChange={() => setResetId(null)}>
        <AlertDialogContent className="bg-surface border-zinc-800">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Reset Password</AlertDialogTitle>
            <AlertDialogDescription className="text-text-secondary">
              Enter a new password for this writer account.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Input
            type="password"
            value={resetPassword}
            onChange={(e) => setResetPassword(e.target.value)}
            placeholder="New password (min 6 characters)"
            className="bg-black border-zinc-800 text-white focus:border-[#CCFF00]"
            data-testid="reset-password-input"
          />
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-zinc-800 text-white hover:bg-white/10">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleResetPassword} className="bg-[#CCFF00] text-black hover:bg-[#b3e600]" data-testid="confirm-reset-password">
              Update Password
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete dialog */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent className="bg-surface border-zinc-800">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Delete Writer Login</AlertDialogTitle>
            <AlertDialogDescription className="text-text-secondary">
              This writer will no longer be able to log in. Their published articles and writer profile stay untouched.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-zinc-800 text-white hover:bg-white/10">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-500 text-white hover:bg-red-600" data-testid="confirm-delete-account">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
